import { onlyDigits, validateCpf } from '@/components/CpfCnpjInput/utils'
import { normalizeFullName } from './normalize-full-name'

export type ScratchRegisterField = 'name' | 'email' | 'phone' | 'document'

export type ScratchRegisterFieldError = {
  field: ScratchRegisterField
  message: string
}

export type ScratchRegisterFormValues = {
  name: string
  email: string
  phone: string
  document: string
}

const MAX_NAME_LENGTH = 255
const MAX_EMAIL_LENGTH = 255

export function validateFullName(value: string): ScratchRegisterFieldError | null {
  const normalized = normalizeFullName(value)

  if (normalized === '') {
    return { field: 'name', message: 'Informe seu nome completo.' }
  }

  if (normalized.length > MAX_NAME_LENGTH) {
    return { field: 'name', message: 'O nome deve ter no máximo 255 caracteres.' }
  }

  const parts = normalized.split(' ').filter((part) => part.length > 0)
  if (parts.length < 2) {
    return { field: 'name', message: 'Informe nome e sobrenome.' }
  }

  if (!/^[\p{L}\p{M}]+(?:[ '\u2019-][\p{L}\p{M}]+)*$/u.test(normalized)) {
    return { field: 'name', message: 'Informe nome e sobrenome válidos.' }
  }

  return null
}

export function validateEmail(value: string): ScratchRegisterFieldError | null {
  const normalized = value.trim().toLowerCase()

  if (normalized === '') {
    return { field: 'email', message: 'Informe seu e-mail.' }
  }

  if (normalized.length > MAX_EMAIL_LENGTH) {
    return { field: 'email', message: 'O e-mail deve ter no máximo 255 caracteres.' }
  }

  const [local = '', domain = '', extra] = normalized.split('@')
  if (
    normalized.length > 254 ||
    extra !== undefined ||
    local.length > 64 ||
    !/^[a-z0-9!#$%&'*+/=?^_`{|}~.-]+$/i.test(local) ||
    local.startsWith('.') ||
    local.endsWith('.') ||
    local.includes('..') ||
    !/^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?\.[a-z]{2,63}$/i.test(domain) ||
    domain
      .split('.')
      .some(
        (label) => label.length > 63 || !/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i.test(label)
      )
  ) {
    return { field: 'email', message: 'Informe um e-mail válido.' }
  }

  return null
}

export function validateBrazilianMobilePhone(
  value: string
): ScratchRegisterFieldError | null {
  let digits = value.replace(/\D/g, '')
  if (/^55\d{10,11}$/.test(digits)) digits = digits.slice(2)

  if (digits.length === 0) {
    return { field: 'phone', message: 'Informe seu WhatsApp com DDD.' }
  }

  const ddds =
    '11 12 13 14 15 16 17 18 19 21 22 24 27 28 31 32 33 34 35 37 38 41 42 43 44 45 46 47 48 49 51 53 54 55 61 62 63 64 65 66 67 68 69 71 73 74 75 77 79 81 82 83 84 85 86 87 88 89 91 92 93 94 95 96 97 98 99'.split(
      ' '
    )
  const subscriber = digits.slice(2)
  if (
    value.length > 32 ||
    !/^\+?[0-9 ()-]+$/.test(value) ||
    !ddds.includes(digits.slice(0, 2)) ||
    !/^(?:9[0-9]{8}|[2-5][0-9]{7})$/.test(subscriber) ||
    /^([0-9])\1+$/.test(subscriber)
  ) {
    return { field: 'phone', message: 'Informe um WhatsApp válido com DDD.' }
  }

  return null
}

export function validateCpfField(value: string): ScratchRegisterFieldError | null {
  const digits = onlyDigits(value)

  if (digits.length === 0) {
    return { field: 'document', message: 'Informe seu CPF.' }
  }

  if (!/^(?:\d{11}|\d{3}\.\d{3}\.\d{3}-\d{2})$/.test(value) || !validateCpf(digits)) {
    return { field: 'document', message: 'Informe um CPF válido.' }
  }

  return null
}

export function validateScratchRegisterForm(
  values: ScratchRegisterFormValues
): ScratchRegisterFieldError[] {
  const validators = [
    validateFullName(values.name),
    validateEmail(values.email),
    validateBrazilianMobilePhone(values.phone),
    validateCpfField(values.document),
  ]

  return validators.filter((error): error is ScratchRegisterFieldError => error !== null)
}

export function validateScratchRegisterField(
  field: ScratchRegisterField,
  values: ScratchRegisterFormValues
): ScratchRegisterFieldError | null {
  switch (field) {
    case 'name':
      return validateFullName(values.name)
    case 'email':
      return validateEmail(values.email)
    case 'phone':
      return validateBrazilianMobilePhone(values.phone)
    case 'document':
      return validateCpfField(values.document)
    default: {
      const exhaustiveCheck: never = field
      return exhaustiveCheck
    }
  }
}
